import { L } from '@lib';
import { Customer } from '@services';
import React from 'react';
import { ToggleChip, ToggleChipRow } from '../Button/ToggleChip';
import { PRIMARY } from '../Theme/palette';

export type NotifyChannel = 'Sms' | 'Push' | 'Email';

/**
 * The channels a customer can be reached on right now (all of them while there is no customer yet,
 * so the choice can be made before one is picked - it is trimmed to what they can use then):
 * Push needs the app, Email an address on file, SMS a mobile number. (Whether the garage has SMS
 * credits is only known to the server, which says so after the booking is made.)
 */
export const availableNotifyChannels = (customer?: Pick<Customer, 'userId' | 'email' | 'phone'> | null) =>
  customer
    ? { Sms: !!customer.phone, Push: !!customer.userId, Email: !!customer.email }
    : { Sms: true, Push: true, Email: true };

/** What is on to begin with: the free channels the customer can be reached on. SMS costs credits, so it is a choice. */
export const defaultNotifyChannels = (customer?: Pick<Customer, 'userId' | 'email' | 'phone'> | null): NotifyChannel[] => {
  if (!customer) return [];
  const available = availableNotifyChannels(customer);
  return (['Push', 'Email'] as NotifyChannel[]).filter((channel) => available[channel]);
};

interface IProps {
  customer?: Pick<Customer, 'userId' | 'email' | 'phone'> | null;
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  /** `small` is the compact one for the mobile drawer. */
  size?: 'small' | 'middle' | 'large';
}

/**
 * How to tell the customer about a booking or estimate: a toggle per channel, any combination, each
 * sent straight away - the same toggles the side panel's filters use. A channel the customer cannot
 * be reached on is greyed, with the reason on hover.
 */
export const NotifyChannelsSelect: React.FC<IProps> = ({ customer, value = [], onChange, disabled, size }) => {
  const available = availableNotifyChannels(customer);

  const channels: { value: NotifyChannel; label: string; unavailable: string; hint: string }[] = [
    { value: 'Push', label: L('App'), unavailable: L('Not an app user'), hint: L('App notification') },
    { value: 'Email', label: L('Email'), unavailable: L('No email address'), hint: L('Email') },
    { value: 'Sms', label: L('SMS'), unavailable: L('No mobile number'), hint: L('SMS - uses credits') },
  ];

  const toggle = (channel: NotifyChannel) =>
    onChange?.(value.includes(channel) ? value.filter((x) => x !== channel) : [...value, channel]);

  return (
    <ToggleChipRow $columns="repeat(3, minmax(0, 1fr))">
      {channels.map((channel) => (
        <ToggleChip
          key={channel.value}
          type="button"
          $size={size === 'small' ? 'small' : 'large'}
          $on={value.includes(channel.value) && available[channel.value]}
          $color={PRIMARY}
          disabled={disabled || !available[channel.value]}
          title={available[channel.value] ? channel.hint : channel.unavailable}
          onClick={() => toggle(channel.value)}
        >
          {channel.label}
        </ToggleChip>
      ))}
    </ToggleChipRow>
  );
};
